import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { ApiError, apiRequest } from '../api/client.js';
import { isBrowserSourceRoute } from '../browser-source.js';
import type { Me, Permission, Workspace } from '../api/types.js';

/**
 * Console session state (ARCHITECTURE.md §11, §4.2).
 *
 * The browser holds no credential. `GET /auth/me` is the only way the Console
 * learns who it is, and the HttpOnly cookie is what makes that call work.
 *
 * Permissions here exist ONLY to decide what to render. §4.2 keeps the backend
 * authoritative: every action is re-checked server-side, so a tampered client
 * gains nothing by pretending it holds a permission.
 */

export type SessionStatus = 'loading' | 'anonymous' | 'authenticated';

export type SessionValue = {
  status: SessionStatus;
  me: Me | null;
  /** Workspace the session is acting in, if one has been selected. */
  activeWorkspace: Workspace | null;
  /** Render-time convenience only; never a security decision. */
  can: (permission: Permission) => boolean;
  refresh: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  activateWorkspace: (workspaceId: string) => Promise<void>;
};

const SessionContext = createContext<SessionValue | undefined>(undefined);

export function SessionProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const [status, setStatus] = useState<SessionStatus>('loading');
  const [me, setMe] = useState<Me | null>(null);

  const refresh = useCallback(async (): Promise<void> => {
    try {
      const next = await apiRequest<Me>('/auth/me');
      setMe(next);
      setStatus('authenticated');
    } catch (error) {
      if (error instanceof ApiError && error.isUnauthenticated) {
        setMe(null);
        setStatus('anonymous');
        return;
      }
      // A server or network fault is not proof of being signed out, but the
      // Console cannot render an authenticated view without a user either.
      setMe(null);
      setStatus('anonymous');
    }
  }, []);

  useEffect(() => {
    /*
     * A browser source has no session and must not ask for one.
     *
     * `SessionProvider` wraps every route, so the alert overlay and the widget
     * runtime were each firing `/auth/me` and `/preferences` on load and taking
     * two 401s — observed in a real browser on 2026-09-11. Harmless to
     * security (the API refuses them) and wrong in three ways that matter:
     * §103.6 says the browser source carries its token and nothing else, §77
     * asks it to stay light for a page that is open for hours, and an operator
     * previewing a widget in their own signed-in browser would send their
     * session cookie to a page that has no use for it.
     *
     * The check is shared with the other global providers — see
     * `browser-source.ts` for why it is one function rather than three copies.
     */
    if (isBrowserSourceRoute()) {
      setStatus('anonymous');
      return;
    }

    void refresh();
  }, [refresh]);

  const signIn = useCallback(
    async (email: string, password: string): Promise<void> => {
      await apiRequest('/auth/login', { method: 'POST', body: { email, password } });
      await refresh();
    },
    [refresh],
  );

  const signOut = useCallback(async (): Promise<void> => {
    try {
      await apiRequest('/auth/logout', { method: 'POST' });
    } catch {
      /*
       * Deliberately swallowed. Signing out always succeeds from the user's
       * point of view: local state is cleared below either way, and there is no
       * useful recovery to offer.
       *
       * It must not REJECT either. Callers invoke this from a click handler as
       * `void signOut()`, so a rejection would surface as an unhandled promise
       * rejection in the browser rather than as anything a user could act on.
       */
    } finally {
      // Clear local state regardless; a UI still showing a signed-in user
      // after they pressed Sign out would be worse than a failed request.
      setMe(null);
      setStatus('anonymous');
    }
  }, []);

  const activateWorkspace = useCallback(
    async (workspaceId: string): Promise<void> => {
      await apiRequest('/workspaces/activate', { method: 'POST', body: { workspaceId } });
      await refresh();
    },
    [refresh],
  );

  const value = useMemo<SessionValue>(() => {
    const permissions = new Set<Permission>(me?.permissions ?? []);
    const activeWorkspace =
      me?.workspaces.find((workspace) => workspace.id === me.activeWorkspaceId) ?? null;

    return {
      status,
      me,
      activeWorkspace,
      can: (permission) => permissions.has(permission),
      refresh,
      signIn,
      signOut,
      activateWorkspace,
    };
  }, [status, me, refresh, signIn, signOut, activateWorkspace]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const value = useContext(SessionContext);

  if (value === undefined) {
    throw new Error('useSession must be used inside a SessionProvider');
  }
  return value;
}
